/* eslint-disable max-lines */
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import usePageIndex from "../../hooks/usePageIndex";
import { useSearch } from "../../hooks/useSearch";
import { ICustomReservationThreshold } from "../../interfaces/endpoints/ICustomReservationThreshold";
import {
  deleteThreshold,
  getAllThresholds
} from "../../services/CustomReservationThreshold.service";
import {
  getCurrentLocation,
  getCurrentStation
} from "../../services/Locations.service";
import { getCurrentOperators } from "../../services/Operators.service";
import IconBtn from "../ui/IconBtn";
import Table, { Row } from "../ui/table";
import DeleteThreshold from "./DeleteThresholdModal";

const columns: (string | number)[] = [
  "Tipi",
  "Nisja",
  "Arritja",
  "Operatori",
  "Pragu (min)",
  "Veprimet"
];

const ReservationThresholds = () => {
  const { response, loading, setResponse } = useSearch(
    getAllThresholds,
    { withQuerySearch: true }
  );
  const { pageIndex } = usePageIndex();
  const [stationNames, setStationNames] = useState<
    Record<number, string>
  >({});
  const [cityNames, setCityNames] = useState<
    Record<number, string>
  >({});
  const [operatorNames, setOperatorNames] = useState<
    Record<number, string>
  >({});

  useEffect(() => {
    if (!response?.data?.length) return;

    const stationIds = new Set<number>();
    const cityIds = new Set<number>();
    const operatorIds = new Set<number>();

    response.data.forEach((row: ICustomReservationThreshold) => {
      if (
        row.fromStationId !== null &&
        !stationNames[row.fromStationId]
      ) {
        stationIds.add(row.fromStationId);
      }
      if (
        row.toStationId !== null &&
        !stationNames[row.toStationId]
      ) {
        stationIds.add(row.toStationId);
      }
      if (
        row.fromCityId !== null &&
        !cityNames[row.fromCityId]
      ) {
        cityIds.add(row.fromCityId);
      }
      if (
        row.toCityId !== null &&
        !cityNames[row.toCityId]
      ) {
        cityIds.add(row.toCityId);
      }
      if (
        row.operatorId !== null &&
        !operatorNames[row.operatorId]
      ) {
        operatorIds.add(row.operatorId);
      }
    });

    const fetchStations = async () => {
      const results: Record<number, string> = {};
      await Promise.all(
        Array.from(stationIds).map(async (id) => {
          try {
            const res = await getCurrentStation(id);
            if (res.data) {
              results[id] = res.data.stationName;
            }
          } catch {
            // fallback handled in render
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setStationNames((prev) => ({ ...prev, ...results }));
      }
    };

    const fetchCities = async () => {
      const results: Record<number, string> = {};
      await Promise.all(
        Array.from(cityIds).map(async (id) => {
          try {
            const res = await getCurrentLocation(id);
            if (res.data) {
              results[id] = res.data.locationName;
            }
          } catch {
            // fallback handled in render
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setCityNames((prev) => ({ ...prev, ...results }));
      }
    };

    const fetchOperators = async () => {
      const results: Record<number, string> = {};
      await Promise.all(
        Array.from(operatorIds).map(async (id) => {
          try {
            const res = await getCurrentOperators(id);
            if (res.data) {
              results[id] = res.data.operatorName;
            }
          } catch {
            // fallback handled in render
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setOperatorNames((prev) => ({ ...prev, ...results }));
      }
    };

    if (stationIds.size > 0) void fetchStations();
    if (cityIds.size > 0) void fetchCities();
    if (operatorIds.size > 0) void fetchOperators();
  }, [response]);

  const handleDelete = (id: number) => {
    deleteThreshold(id)
      .then(() => {
        toast.success("Pragu u fshi me sukses.", {
          position: "top-right"
        });
        setResponse((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.filter(
                  (el: ICustomReservationThreshold) =>
                    el.id !== id
                )
              }
            : null
        );
      })
      .catch(() =>
        toast.error("Ndodhi nje gabim gjate fshirjes.")
      );
  };

  const renderName = (
    id: number | null,
    names: Record<number, string>
  ) => {
    if (id === null) return null;
    const name = names[id];
    if (name) return name;
    return (
      <span className="text-gray-400 text-xs">#{id}</span>
    );
  };

  const renderEndpoint = (row: ICustomReservationThreshold, side: "from" | "to") => {
    if (row.fromCityId !== null) {
      const id = side === "from" ? row.fromCityId : row.toCityId;
      return renderName(id, cityNames);
    }
    const id = side === "from" ? row.fromStationId : row.toStationId;
    return renderName(id, stationNames);
  };

  const renderOperator = (row: ICustomReservationThreshold) => {
    if (row.fromCityId !== null) {
      return <span className="text-gray-400 italic">-</span>;
    }
    if (row.operatorId === null) {
      return (
        <span className="text-gray-400 italic">
          Te gjithe
        </span>
      );
    }
    const name = operatorNames[row.operatorId];
    if (name) return name;
    return (
      <span className="text-gray-400 text-xs">
        #{row.operatorId}
      </span>
    );
  };

  return (
    <div
      className="flex flex-col"
      style={{ height: "calc(100vh - 11rem)" }}
    >
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {response?.data?.length ? (
          response.data.map(
            (row: ICustomReservationThreshold, i: number) => (
              <Row
                key={i}
                type={
                  row.fromCityId !== null ? "Qytet" : "Stacion"
                }
                from={renderEndpoint(row, "from")}
                to={renderEndpoint(row, "to")}
                operator={renderOperator(row)}
                threshold={
                  <span className="font-semibold">
                    {row.threshold} min
                  </span>
                }
                actions={
                  <span className="flex items-center">
                    <Link
                      to={`/reservation-thresholds/edit/${row.id}`}
                      state={{
                        crumbs: [
                          "Reservation Thresholds",
                          "Modifiko pragun"
                        ],
                        page: pageIndex,
                        thresholdData: row
                      }}
                    >
                      <IconBtn className="w-6 h-6">
                        <PencilSquareIcon className="w-4 h-4" />
                      </IconBtn>
                    </Link>
                    <DeleteThreshold
                      handleDelete={() => handleDelete(row.id)}
                    />
                  </span>
                }
              />
            )
          )
        ) : (
          <></>
        )}
      </Table>
    </div>
  );
};

export default ReservationThresholds;
